// components/notifications/NotificationSettings.jsx - Add mobile info
import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Volume2, VolumeX, Globe, Moon, Check, Smartphone, Laptop } from 'lucide-react';
import { isMobileDevice, requestNotificationPermission } from '../../utils/notificationUtils';
import TestNotificationButton from './TestNotificationButton';

const NotificationSettings = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    sound: true,
    desktop: true,
    email: false,
    quietHours: false,
    quietStart: '22:00',
    quietEnd: '08:00'
  });
  
  const [permissionStatus, setPermissionStatus] = useState('default');
  const isMobile = isMobileDevice();

  useEffect(() => {
    loadSettings();
    checkPermission();
  }, []);

  const loadSettings = () => {
    const saved = localStorage.getItem('notification_settings');
    if (saved) {
      try {
        setSettings(JSON.parse(saved));
      } catch (e) {
        // console.error('Error loading settings:', e);
      }
    }
  };

  const saveSettings = (newSettings) => {
    setSettings(newSettings);
    localStorage.setItem('notification_settings', JSON.stringify(newSettings));
  };

  const checkPermission = () => {
    if (!('Notification' in window)) {
      setPermissionStatus('unsupported');
      return;
    }
    setPermissionStatus(Notification.permission);
  };

  const requestPermission = async () => {
    try {
      const granted = await requestNotificationPermission(true);
      setPermissionStatus(granted ? 'granted' : 'denied');
      
      if (granted) {
        saveSettings({ ...settings, enabled: true });
        
        // Show test notification on desktop
        if (!isMobile) {
          new Notification('✅ Notifications Enabled', {
            body: 'You will now receive notifications',
            icon: '/favicon.ico'
          });
        } else {
          // Show toast for mobile
          const event = new CustomEvent('showToast', {
            detail: {
              title: '✅ Notifications Enabled',
              message: 'You will now receive real-time updates',
              type: 'system'
            }
          });
          window.dispatchEvent(event);
        }
      }
    } catch (error) {
      // console.error('Error requesting permission:', error);
    }
  };

  const toggleSetting = (key) => {
    const newSettings = { ...settings, [key]: !settings[key] };
    saveSettings(newSettings);
    
    if (key === 'sound' && newSettings.sound) {
      testSound();
    }
  };

  const testSound = () => {
    const audio = new Audio('/sounds/notification.mp3');
    audio.volume = 0.3;
    audio.play().catch(e =>  console.log('Sound test failed:', e));
  };

  if (permissionStatus === 'unsupported') {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <BellOff className="w-5 h-5 text-yellow-600" />
          <div>
            <h4 className="font-medium text-yellow-800">Notifications Not Supported</h4>
            <p className="text-sm text-yellow-700">
              Your browser doesn't support desktop notifications
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Mobile Info Banner */}
      {isMobile && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <Smartphone className="w-4 h-4 text-blue-600" />
            <p className="text-xs text-blue-800">
              On mobile, notifications appear as in-app toasts. For best experience, keep the app open.
            </p>
          </div>
        </div>
      )}
      
      {/* Main Toggle */}
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-3">
          {settings.enabled ? (
            <Bell className="w-5 h-5 text-green-500" />
          ) : (
            <BellOff className="w-5 h-5 text-gray-400" />
          )}
          <div>
            <h3 className="font-medium text-gray-900">Push Notifications</h3>
            <p className="text-sm text-gray-600">
              {isMobile ? 'Get in-app alerts for updates' : 'Get real-time alerts on your device'}
            </p>
          </div>
        </div>
        
        {permissionStatus === 'granted' ? (
          <button
            onClick={() => toggleSetting('enabled')}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              settings.enabled ? 'bg-green-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        ) : (
          <button
            onClick={requestPermission}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm hover:bg-orange-600"
          >
            Enable Notifications
          </button>
        )}
      </div>

      {settings.enabled && permissionStatus === 'granted' && (
        <>
          {/* Sound Toggle - Only show on desktop or if mobile supports sound */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              {settings.sound ? (
                <Volume2 className="w-5 h-5 text-gray-600" />
              ) : (
                <VolumeX className="w-5 h-5 text-gray-400" />
              )}
              <div>
                <h3 className="font-medium text-gray-900">Notification Sound</h3>
                <p className="text-sm text-gray-600">Play sound for new notifications</p>
              </div>
            </div>
            <button
              onClick={() => toggleSetting('sound')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.sound ? 'bg-green-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.sound ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Test Button */}
          <TestNotificationButton />
        </>
      )}
    </div>
  );
};

export default NotificationSettings;